/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ContactData } from '../@types/contact';
import type { IContent, IMinimalEvent } from '../@types/event';
import type { RoomCategory } from '../@types/room';
import type { ResponseCursor } from '../http';
import type { Hero } from '../models/room/room-summary';
import type { Logger } from '../utils/logger';

/**
 * Events which can be fired by the SlidingSync class. These are designed to provide different levels
 * of information when processing sync responses.
 *  - RoomData: concerns rooms, useful for SlidingSyncSdk to update its knowledge of rooms.
 *  - Lifecycle: concerns callbacks at various well-defined points in the sync process.
 * Specifically, the order of event invocation is:
 *  - Lifecycle (state=RequestFinished)
 *  - RoomData (N times)
 *  - Lifecycle (state=Complete)
 */
export enum SlidingSyncEvent {
  /**
   * This event fires when there are updates for a room. Fired as and when rooms are encountered
   * in the response.
   */
  RoomData = 'SlidingSync.RoomData',
  /**
   * This event fires at various points in the /sync loop lifecycle.
   *  - SlidingSyncState.RequestFinished: Fires after we receive a valid response but before the
   * response has been processed. Perform any pre-process steps here. If there was a problem syncing,
   * `err` will be set (e.g network errors).
   *  - SlidingSyncState.Complete: Fires after the response has been processed.
   */
  Lifecycle = 'SlidingSync.Lifecycle',
}

export type IRoomEvent = {
  id: string;
  sender_id: string;
  timestamp: number;
  created_at: string;
} & IMinimalEvent;

export type IStateEvent = {
  prev_content?: IContent;
  state_key: string;
} & IRoomEvent;

/**
 * @example - Data from server
 * ```json
 *  {
 *      "category": "chat_room",
 *      "id": "ca5190ed-da0a-4537-8884-f1ad93677667",
 *      "updated_at": "2025-10-08T10:32:57.556Z",
 *      "created_at": "2025-09-09T03:00:22.837Z",
 *      "title": "",
 *      "name": "Hội những người tin loài bò biết bay",
 *      "description": "Alloo",
 *      "media": [
 *          {
 *              "type": "image",
 *              "url": "https://i.pinimg.com/1200x/fd/32/4c/fd324c0256ee7cd8ec15287e931f4f73.jpg"
 *          }
 *      ],
 *      "type": "",
 *      "info": "",
 *      "value": "",
 *      "owner_id": "",
 *      "content": "",
 *      "likes": 0,
 *      "tags": null,
 *      "status": "",
 *      "participants": [
 *          "d228d6ed-d32c-4d13-88c6-ed334b6718d7",
 *          "9015335e-c8a2-44ab-914b-1fc34b38dbbb"
 *      ],
 *      "is_allow_comment": false,
 *      "message_new_count": 0,
 *      "last_message": {
 *          "id": "d8cb4155-92ac-4ed0-8dcd-73c140867d2b",
 *          "room_id": "ca5190ed-da0a-4537-8884-f1ad93677667",
 *          "sender_id": "9015335e-c8a2-44ab-914b-1fc34b38dbbb",
 *          "content": "Alloo",
 *          "type": "text",
 *          "participants_readed": null,
 *          "created_at": "2025-10-08T10:32:57.555Z",
 *          "updated_at": "2025-10-08T10:32:57.555Z",
 *          "timestamp": "2025-10-08T10:32:57.555Z"
 *      },
 *      "total_comments": 0,
 *      "liked": false,
 *      "is_read": false
 *    }
 * ```
 * {@link ../@types/event!IEvent}
 */
export type RoomData = {
  category: RoomCategory;
  name: string;
  required_state: IStateEvent[];
  timeline: (IRoomEvent | IStateEvent)[];
  id: string;
  title: string;
  description: string;
  media: { type: string; url: string }[];
  type: string;
  info: string;
  tags: string[] | null;
  participants: string[];
  is_allow_comment: boolean;
  message_new_count: number;
  last_message: IRoomEvent | IStateEvent | null;
  total_comments: number;
  liked: boolean;
  is_read: boolean;
  likes: number;
  created_at: string;
  updated_at: string;
  initial: boolean;
  content: string;
  members: ContactData[];
  notification_id?: string;
  /**
   * True if the timeline is limited (i.e. does not contain the full timeline of the room)
   * True if this is a non-continuous timeline, and there are older messages preceding it.
   */
  limited?: boolean;
  prev_batch?: string;
  num_live?: number;
  bump_stamp?: number;
  heroes?: Hero[];
};

export enum SlidingSyncState {
  /**
   * Fired by SlidingSyncEvent.Lifecycle event immediately before processing the response.
   * Right now when the response has been received but not yet processed.
   */
  RequestFinished = 'FINISHED',
  /**
   * Fired by SlidingSyncEvent.Lifecycle event immediately after all room data listeners have been
   * invoked, but before list listeners.
   * This indicates that the sync processing has fully completed.
   */
  Complete = 'COMPLETE',
}

export type ListResponse = {
  /**
   * The total of rooms in this list on the server
   */
  count?: number;
  /**
   * True if there are more rooms available on the server for this list
   */
  has_next?: boolean;
};

/**
 * A complete Sliding Sync response
 */
export type SlidingSyncResponse = {
  pos: string;
  txn_id?: string;
  lists: Record<string, ListResponse>;
  rooms: Record<string, RoomData>;
  extensions: Record<string, object>;
  firstLoadFromServer?: boolean;
};

export type RoomsResponse = ResponseCursor<RoomData[], 'items'>;

export type SlidingSyncEventHandlerMap = {
  [SlidingSyncEvent.RoomData]: (roomId: string, roomData: RoomData) => Promise<void> | void;
  [SlidingSyncEvent.Lifecycle]: (state: SlidingSyncState, resp: SlidingSyncResponse | null, err?: Error) => void;
};

export type ResetTimelineCallback = (roomId: string) => boolean;

/**
 * Represents a list subscription.
 */
export type SlidingSyncList = {
  ranges: number[][];
  sort?: string[];
  slow_get_all_rooms?: boolean;
};

/**
 * Options passed into the constructor of SyncApi by XHubChatClient
 */
export type SyncApiOptions = {
  /**
   * A function which is called
   * with a room ID and returns a boolean. It should return 'true' if the SDK can
   * SAFELY remove events from this room. It may not be safe to remove events if
   * there are other references to the timelines for this room.
   */
  canResetEntireTimeline?: ResetTimelineCallback;

  /** Logger instance to use for writing debug logs. */
  logger: Logger;
};

export enum SyncState {
  /**
   * Emitted after we try to sync more than `FAILED_SYNC_ERROR_THRESHOLD`
   * times and are still failing. Or when we encounter a hard error like the
   * token being invalid.
   */
  Error = 'ERROR',
  /**
   * Emitted after the first sync events are ready (this could even be sync
   * events from the cache)
   */
  Prepared = 'PREPARED',
  /** Emitted when the sync loop is no longer running */
  Stopped = 'STOPPED',
  /** Emitted after each sync request happens */
  Syncing = 'SYNCING',
  /** Emitted after a connectivity error and we're ready to start syncing again */
  Catchup = 'CATCHUP',
  /**
   * Emitted for each time we try reconnecting. Will switch to `Error` after
   * we reach the `FAILED_SYNC_ERROR_THRESHOLD`
   */
  Reconnecting = 'RECONNECTING',
}

export type ISyncStateData = {
  /**
   * The error if `state=ERROR`.
   */
  error?: Error;
  /**
   * The 'since' token passed to /sync.
   *    `null` for the first successful sync since this client was
   *    started. Only present if `state=PREPARED` or
   *    `state=SYNCING`.
   */
  oldSyncToken?: string;
  /**
   * The 'next_batch' result from /sync, which
   *    will become the 'since' token for the next call to /sync. Only present if
   *    `state=PREPARED` or `state=SYNCING`.
   */
  nextSyncToken?: string;
  /**
   * True if we are working our way through a
   *    backlog of events after connecting. Only present if `state=SYNCING`.
   */
  catchingUp?: boolean;
  fromCache?: boolean;
};

/**
 * WebSocket connection state
 */
export enum ConnectionState {
  Connected = 'connected',
  Disconnected = 'disconnected',
  Reconnecting = 'reconnecting',
  Error = 'error',
}

/**
 * Queued message for offline support
 */
export type QueuedMessage = {
  id: string;
  roomId: string;
  data: any;
  timestamp: number;
  retries: number;
};

/**
 * Reconnection strategy configuration
 */
export type ReconnectionStrategy = {
  attempt: number;
  delay: number;
  maxAttempts: number;
  shouldRetry: boolean;
};
